import { useEffect, useMemo, useRef, type ReactNode } from 'react'
import { Loader2, Undo2, Check, Zap } from 'lucide-react'
import { useCompanionActionContext } from '@/features/companion/companion-action-hooks'
import { usePanelSelectionStore } from '@/stores/panel-selection-store'
import { useEditDiffStore } from '@/features/companion/edit-diff-store'
import { ChatMessage } from './chat-message'
import { AnalystFeedback } from './analyst-feedback'
import { collectTickers } from '@/components/cashtag-highlight'
import { EditDiffCard } from './edit-diff-card'
import { PendingWalkthroughAction } from './pending-walkthrough-action'
import { RequestGuideButton } from './request-guide-button'
import { useDocGapStore } from '@/features/companion/doc-gap-store'
import { cn } from '@/lib/utils'
import type { CompanionConversation } from './use-companion-conversation'

// ── Conversation body — shared by every companion shell ────

interface ChatConversationBodyProps {
  conversation: CompanionConversation
  /** Rendered when there are no messages and nothing is streaming. */
  emptyState: ReactNode
  /** Shells pass their open/expanded flag so opening snaps to the newest message. */
  visible: boolean
  /** Show only messages from this index on (the floating window's
   *  fresh-on-reopen watermark). Default 0 = full history (the drawer). */
  startIndex?: number
}

const nowMs = () => Date.now()

/**
 * The scrollable message area every companion shell shares: fade masks,
 * message list with analyst feedback + the "Do it →" nudge, edit-diff cards,
 * the live-typing bubble, and the thinking spinner. Shells differ only in
 * chrome and empty state.
 */
export function ChatConversationBody({
  conversation,
  emptyState,
  visible,
  startIndex = 0,
}: ChatConversationBodyProps) {
  const { messages, isProcessing, toolCount, streamingText, handleDoIt } = conversation
  // A how-to topic this turn found no guide for: the latest answer offers
  // "Request this" (doc-gap-store.ts).
  const docGapTopic = useDocGapStore((s) => s.topic)
  // The watermark slice: shells that open "fresh" (the floating window) pass
  // the index of the first message that belongs to this session.
  const visibleMessages = startIndex > 0 ? messages.slice(startIndex) : messages
  // Symbols established anywhere in the conversation (cashtags, ALL-CAPS,
  // STT near-caps) — lets lowercase mentions ("qnrx" in a lowercase-styled
  // answer) highlight everywhere, including in messages ABOVE the one that
  // established the symbol.
  const knownTickers = useMemo(() => {
    const known = new Set<string>()
    for (const m of messages) for (const t of collectTickers(m.text)) known.add(t)
    return known
  }, [messages])
  const diffCards = useEditDiffStore((s) => s.cards)
  const keepCard = useEditDiffStore((s) => s.keepCard)
  const undoCard = useEditDiffStore((s) => s.undoCard)
  const messagesEndRef = useRef<HTMLDivElement>(null)

  // ── Auto-scroll on new messages or when the shell opens ─────

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, streamingText])

  useEffect(() => {
    if (visible) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'instant' })
    }
  }, [visible])

  return (
    <div className="relative flex-1 min-h-0">
      {/* Top fade */}
      <div className="pointer-events-none absolute inset-x-0 top-0 z-10 h-6 bg-gradient-to-b from-background to-transparent" />
      {/* Bottom fade */}
      <div className="pointer-events-none absolute inset-x-0 bottom-0 z-10 h-6 bg-gradient-to-t from-background to-transparent" />
      <div className="h-full overflow-y-auto">
        {visibleMessages.length === 0 && !streamingText && !isProcessing ? (
          emptyState
        ) : (
          <div className="py-2">
            {visibleMessages.map((msg, sliceIndex) => {
              // Absolute index into the full history — keys and the
              // last-message check must survive the watermark slice.
              const i = sliceIndex + startIndex
              const answerEventId =
                msg.role === 'assistant'
                  ? msg.toolResults?.find((r) => r.answerEventId)?.answerEventId
                  : undefined
              return (
                <div key={`${msg.timestamp}-${i}`}>
                  <ChatMessage
                    role={msg.role}
                    text={msg.text}
                    timestamp={msg.timestamp}
                    knownTickers={knownTickers}
                    toolCalls={
                      msg.toolCalls as Array<{ name: string; input: Record<string, unknown> }>
                    }
                    toolResults={msg.toolResults as Array<{ success: boolean; content: string }>}
                  />
                  {/* Analyst 👍/👎 feedback — only on analyst-sourced assistant messages */}
                  {answerEventId && <AnalystFeedback answerEventId={answerEventId} />}
                  {docGapTopic &&
                    !isProcessing &&
                    msg.role === 'assistant' &&
                    i === messages.length - 1 && (
                      <div className="px-3 pb-2">
                        <RequestGuideButton
                          topic={docGapTopic}
                          question={
                            [...messages.slice(0, i)].reverse().find((m) => m.role === 'user')
                              ?.text ?? docGapTopic
                          }
                          page={conversation.page}
                        />
                      </div>
                    )}
                  {/* The "Do it" nudge: only on the latest answer, from a
                      read-only turn, when the turn wanted an action. It
                      re-sends the request with the turn able to act, on
                      every plan since Build went away (2026-10-16). */}
                  {/* What the answer asks the trader to pick from (Chris,
                      2026-10-18: "let me click the watchlist I want"): only
                      on the latest answer, and a click is the reply. */}
                  {msg.choices?.length && i === messages.length - 1 && !isProcessing ? (
                    <div className="flex flex-wrap gap-1.5 px-3 pb-2" data-testid="answer-choices">
                      {msg.choices.map((choice) => (
                        <button
                          key={choice.label}
                          type="button"
                          onClick={() => conversation.handleSend(choice.label)}
                          className="inline-flex flex-col items-start rounded-md border border-primary/40 bg-primary/10 px-2.5 py-1 text-left text-xs transition-colors hover:bg-primary/20"
                        >
                          <span className="font-medium text-primary">{choice.label}</span>
                          {choice.detail && (
                            <span className="text-[11px] text-muted-foreground">
                              {choice.detail}
                            </span>
                          )}
                        </button>
                      ))}
                    </div>
                  ) : null}
                  {msg.suggestChangeText && i === messages.length - 1 && (
                    <div className="px-3 pb-2">
                      <button
                        type="button"
                        onClick={() => handleDoIt(msg.suggestChangeText!)}
                        className="inline-flex items-center gap-1.5 rounded-md border border-amber-500/40 bg-amber-500/10 px-2.5 py-1 text-xs font-medium text-amber-400 transition-colors hover:bg-amber-500/20"
                      >
                        <Zap size={12} />
                        Do it →
                      </button>
                    </div>
                  )}
                </div>
              )
            })}
            {/* A guided flow the companion found but did not start (its panel
                is not on screen, or the trader only asked about it): the
                thread's pending slot holds it until Start walkthrough. */}
            <PendingWalkthroughAction conversation={conversation} />
            {diffCards.map((card) => (
              <EditDiffCard key={card.batchId} card={card} onKeep={keepCard} onUndo={undoCard} />
            ))}
            {/* Live "typing" bubble — provisional text from text-delta
                events, replaced by the final message when it lands. */}
            {streamingText && (
              <div data-testid="streaming-message">
                <ChatMessage
                  role="assistant"
                  text={streamingText}
                  timestamp={nowMs()}
                  streaming
                  knownTickers={knownTickers}
                />
              </div>
            )}
            {isProcessing && !streamingText && (
              <div className="flex items-center gap-2 px-3 py-2 text-xs text-muted-foreground">
                <Loader2 size={12} className="animate-spin" />
                {toolCount > 0 ? `Working... Tool (${toolCount})` : 'Thinking...'}
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
        )}
      </div>
    </div>
  )
}

// ── Action banners — shared by every companion shell ────────

/**
 * The panel-selection banner and the applied-action Keep/Undo banner. Kept
 * together because every shell renders them in the same slot, directly above
 * the input.
 */
export function ChatActionBanners() {
  const { pendingAction, pendingProposal, approveAction, revertAction } =
    useCompanionActionContext()
  const panelSelectionActive = usePanelSelectionStore((s) => s.active)
  const panelSelectionMessage = usePanelSelectionStore((s) => s.message)
  const cancelPanelSelection = usePanelSelectionStore((s) => s.cancelSelection)

  return (
    <>
      {/* Panel selection banner */}
      {panelSelectionActive && (
        <div className="flex items-center gap-2 bg-primary/10 px-3 py-2.5">
          <div className="h-2 w-2 shrink-0 animate-pulse rounded-full bg-primary" />
          <span className="flex-1 text-xs text-foreground">{panelSelectionMessage}</span>
          <button
            onClick={cancelPanelSelection}
            className="rounded px-2 py-1 text-xs text-muted-foreground hover:bg-muted"
          >
            Cancel
          </button>
        </div>
      )}

      {/* Ask-first card (2026-10-15): nothing in the batch runs until Apply. */}
      {pendingProposal && (
        <div
          role="group"
          aria-label="Change waiting for your OK"
          className="flex flex-col gap-1.5 border-t border-border bg-primary/10 px-3 py-2"
        >
          <span className="text-[11px] font-medium text-muted-foreground">Waiting for your OK</span>
          <ul className="flex flex-col gap-0.5">
            {pendingProposal.lines.map((line, i) => (
              <li key={i} className="text-xs text-foreground">
                {line}
              </li>
            ))}
          </ul>
          <div className="flex justify-end gap-1">
            <button
              type="button"
              onClick={pendingProposal.cancel}
              className="rounded px-2 py-1 text-xs text-muted-foreground hover:bg-muted"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={pendingProposal.apply}
              className="flex items-center gap-1 rounded bg-primary px-2 py-1 text-xs text-primary-foreground hover:bg-primary/90"
            >
              <Check size={12} />
              Apply
            </button>
          </div>
        </div>
      )}

      {/* Undo banner */}
      {pendingAction && (
        <div
          className={cn(
            'flex items-center gap-2 border-t border-border px-3 py-2',
            pendingAction.partial ? 'bg-amber-500/15' : 'bg-primary/15',
          )}
        >
          <span className="flex-1 text-xs text-muted-foreground">
            {pendingAction.partial ? 'Some steps failed — review or undo' : 'Action applied'}
          </span>
          {revertAction && (
            <button
              onClick={revertAction}
              className="flex items-center gap-1 rounded px-2 py-1 text-xs text-destructive hover:bg-destructive/10"
            >
              <Undo2 size={12} />
              Undo
            </button>
          )}
          {approveAction && (
            <button
              onClick={approveAction}
              className="flex items-center gap-1 rounded px-2 py-1 text-xs text-primary hover:bg-primary/10"
            >
              <Check size={12} />
              Keep
            </button>
          )}
        </div>
      )}
    </>
  )
}
